import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * 검색 조건 표(QA-348, 고도몰 검색 영역 형태) — 헤더 칸 | 입력 칸 | 헤더 칸 | 입력 칸 의 4열 격자.
 * 헤더 칸은 옅은 배경 + 굵은 글자로 입력 칸과 구분하고, 칸 크기가 일정해 값이 바뀌어도(예: 카테고리 선택)
 * 다른 항목이 밀리지 않는다. 범위(최소~최대, 시작~종료)처럼 넓어야 하는 항목은 span="full" 로 한 줄 전체를 쓴다.
 * 좁은 화면(md 미만)에서는 헤더 | 입력 2열로 접힌다.
 */
export const SearchGrid = ({
  children,
  footer,
  className,
  'aria-label': ariaLabel,
  testId,
}: {
  children: ReactNode;
  /** 표 아래 가운데 버튼 영역(초기화 등). */
  footer?: ReactNode;
  className?: string;
  'aria-label'?: string;
  testId?: string;
}) => (
  <section aria-label={ariaLabel} className={cn('flex flex-col gap-3', className)} data-testid={testId}>
    <div
      className="grid grid-cols-[110px_minmax(0,1fr)] overflow-hidden rounded-lg md:grid-cols-[120px_minmax(0,1fr)_120px_minmax(0,1fr)]"
      style={{ border: '1px solid var(--divider-strong)', background: 'var(--surface-raised)' }}
    >
      {children}
    </div>
    {footer ? <div className="flex justify-center gap-2">{footer}</div> : null}
  </section>
);

export const SearchGridItem = ({
  label,
  children,
  span = 'half',
  htmlFor,
  testId,
}: {
  label: ReactNode;
  children: ReactNode;
  /** half = 헤더+입력 한 쌍(한 줄에 두 쌍), full = 입력 칸이 줄 끝까지. */
  span?: 'half' | 'full';
  htmlFor?: string;
  testId?: string;
}) => (
  <>
    <div
      className="flex items-center px-3 py-2 text-[12px] font-semibold"
      style={{ background: 'var(--surface-1)', color: 'var(--content-strong)', borderBottom: '1px solid var(--divider-soft)' }}
      data-testid={testId ? `${testId}-label` : undefined}
    >
      {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
    </div>
    <div
      className={cn('flex min-w-0 items-center px-3 py-2', span === 'full' && 'md:col-span-3')}
      style={{ borderBottom: '1px solid var(--divider-soft)' }}
      data-testid={testId}
    >
      <div className="w-full min-w-0">{children}</div>
    </div>
  </>
);
